import React, { useEffect, useState, useCallback } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

/**
 * AutoReplyPanel — M2.3.4
 * Admin config della risposta automatica WhatsApp (gestita da Sarabi OS via Cloud API).
 * Sostituisce operativamente il "messaggio automatico" della WhatsApp Business App.
 *
 * Contratti UX:
 *  - enabled=true + message vuoto → mostra warning ma consente il salvataggio.
 *  - cooldown_days è configurabile (default 14, range 1–365).
 *  - Nessun dato sensibile: solo enabled/message/cooldown_days.
 */
export function AutoReplyPanel() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [enabled, setEnabled] = useState(false);
  const [message, setMessage] = useState("");
  const [cooldownDays, setCooldownDays] = useState(14);
  const [updatedAt, setUpdatedAt] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/messaging/auto-reply/config");
      setEnabled(!!r.data.enabled);
      setMessage(r.data.message || "");
      setCooldownDays(Number(r.data.cooldown_days || 14));
      setUpdatedAt(r.data.updated_at || null);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (cooldownDays < 1 || cooldownDays > 365) {
      toast.error("Cooldown deve essere tra 1 e 365 giorni.");
      return;
    }
    if (enabled && !message.trim()) {
      const ok = window.confirm(
        "Auto-reply attivo ma il messaggio è vuoto. Nessun invio avverrà. Salvare comunque?"
      );
      if (!ok) return;
    }
    setSaving(true);
    try {
      const r = await api.put("/admin/messaging/auto-reply/config", {
        enabled,
        message,
        cooldown_days: cooldownDays,
      });
      setEnabled(!!r.data.enabled);
      setMessage(r.data.message || "");
      setCooldownDays(Number(r.data.cooldown_days || 14));
      setUpdatedAt(r.data.updated_at || null);
      toast.success("Configurazione salvata.");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const charCount = (message || "").length;

  return (
    <section
      className="card-elevated p-5 mt-6"
      data-testid="auto-reply-panel"
    >
      <div className="flex justify-between items-start gap-4">
        <div>
          <h2 className="text-lg font-semibold text-ink-900">
            Risposta automatica WhatsApp
          </h2>
          <p className="text-sm text-ink-500 mt-1">
            Automazione gestita da <strong>Sarabi OS</strong> via Cloud API.
            Dopo l'attivazione qui, <strong>disattiva</strong> il messaggio
            automatico impostato nella WhatsApp Business App per evitare
            doppie risposte.
          </p>
        </div>
        <label className="inline-flex items-center gap-2 select-none">
          <input
            type="checkbox"
            checked={enabled}
            onChange={(e) => setEnabled(e.target.checked)}
            disabled={loading || saving}
            data-testid="auto-reply-enabled-toggle"
            className="h-5 w-5"
          />
          <span className="text-sm font-medium text-ink-800">
            {enabled ? "Attiva" : "Disattivata"}
          </span>
        </label>
      </div>

      <div className="mt-4 space-y-4">
        <div>
          <label className="block text-sm font-medium text-ink-800 mb-1">
            Messaggio automatico
          </label>
          <textarea
            className="input w-full min-h-[120px] font-mono text-sm"
            placeholder="Es. Grazie per averci scritto! Ti risponderemo appena possibile."
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            disabled={loading || saving}
            maxLength={4096}
            data-testid="auto-reply-message-input"
          />
          <div className="text-xs text-ink-500 mt-1">
            {charCount} / 4096 caratteri
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-[auto_1fr] gap-3 items-center">
          <label
            className="text-sm font-medium text-ink-800"
            htmlFor="auto-reply-cooldown"
          >
            Invia se il cliente non scrive da (giorni)
          </label>
          <div className="flex items-center gap-2">
            <input
              id="auto-reply-cooldown"
              type="number"
              min={1}
              max={365}
              className="input w-24"
              value={cooldownDays}
              onChange={(e) => setCooldownDays(Number(e.target.value) || 0)}
              disabled={loading || saving}
              data-testid="auto-reply-cooldown-input"
            />
            <span className="text-xs text-ink-500">
              Default: 14. Range 1–365.
            </span>
          </div>
        </div>

        {enabled && !message.trim() && (
          <div
            className="bg-amber-50 border-l-4 border-amber-500 rounded p-3 text-sm text-amber-900"
            data-testid="auto-reply-empty-warning"
          >
            Auto-reply attiva ma messaggio vuoto → nessun invio avverrà.
          </div>
        )}
      </div>

      <div className="mt-5 flex items-center justify-between">
        <div className="text-xs text-ink-500">
          {updatedAt
            ? `Ultimo aggiornamento: ${new Date(updatedAt).toLocaleString("it-IT")}`
            : "Non ancora configurata."}
        </div>
        <button
          type="button"
          className="btn-primary"
          onClick={save}
          disabled={loading || saving}
          data-testid="auto-reply-save-btn"
        >
          {saving ? "Salvo…" : "Salva configurazione"}
        </button>
      </div>
    </section>
  );
}

export default AutoReplyPanel;
